import { acknowledgeRecoveryCodes } from '@src/utils/recoveryCodes';
import { createPortal } from 'react-dom';
import { PageHeading } from '@src/components/PageHeading/PageHeading';
import { santaClient } from '@src/state/client';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Cluster,
  Note,
  Readout,
  Stack,
  Typography,
  showToast,
} from '@src/components';
import { classes } from '@src/components/layout/space';
import { CopyIcon } from '@src/components/icons/CopyIcon';
import { StarIcon } from '@src/components/icons/StarIcon';
import { sameOriginUrl } from '@src/routing/urls';
import './AccessCodes.css';

function displayUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname}`;
  } catch {
    return url;
  }
}

export interface AccessCodesProps {
  /**
   * Whose access this is. An organizer is saving their way back into a whole
   * game; a participant only into their own card, and the wording, the avatar
   * and where the button goes all follow from that.
   */
  kind?: 'organizer' | 'participant' | 'account';
  /** The game for an organizer, the person for a participant. */
  subject: string;
  /** The animal on the card, for a participant. */
  picture?: string | null;
  /** Where the codes lead: the box for an organizer, a card for a participant. */
  url: string;
  codes: string[];
  /** Profile identity and recovery instructions included when copying account codes. */
  copyIntroduction?: string;
  /** Flat, undecorated surface for embedding inside an existing settings panel. */
  variant?: 'default' | 'flat';
  /** Optional footer host for dialogs with a scrollable body and fixed actions. */
  actionsContainer?: HTMLElement | null;
  onContinue: (savedAt?: string) => void;
}

/**
 * The one chance to keep access to something created without an account.
 * Organizers with a verified email can continue without saving recovery codes.
 */
export function AccessCodes({
  kind = 'organizer',
  subject,
  url,
  codes,
  copyIntroduction,
  onContinue,
  variant = 'default',
  actionsContainer,
}: AccessCodesProps) {
  const { t } = useTranslation();
  const codeSet = codes.join('|');
  const [savedCodes, setSavedCodes] = useState<string | null>(null);
  const [copiedCodes, setCopiedCodes] = useState<string | null>(null);
  const copied = codes.length > 0 && copiedCodes === codeSet;
  const saved = copied && savedCodes === codeSet;
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  async function continueAfterSaving() {
    if (savingRef.current) return;
    // Verified organizers can skip saving; do not record an acknowledgement for them.
    if (!saved || !copied) {
      if (verifiedEmail) onContinue();
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const savedAt = await acknowledgeRecoveryCodes(codes[0]);
      onContinue(savedAt);
    } catch {
      showToast(t('account.recoveryAcknowledgeError'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }
  const organizer = kind === 'organizer';
  const account = kind === 'account';
  const [verifiedAccount, setVerifiedAccount] = useState<{ url: string; email: string } | null>(
    null,
  );
  const verifiedEmail = organizer && verifiedAccount?.url === url ? verifiedAccount.email : '';
  useEffect(() => {
    let cancelled = false;
    if (organizer) {
      void santaClient
        .account()
        .then((result) => {
          if (!cancelled && result.ok && result.data?.isVerified && result.data.email) {
            setVerifiedAccount({ url, email: result.data.email });
          }
        })
        .catch(() => {
          /* Keep the save requirement if verification is unavailable. */
        });
    }
    return () => {
      cancelled = true;
    };
  }, [organizer, url]);
  const link = sameOriginUrl(url);

  async function copyAll() {
    const heading = organizer
      ? t('box.access.copyHeading', { box: subject })
      : t('box.access.cardCopyHeading', { name: subject });
    const text = account
      ? `${copyIntroduction || t('account.recoveryTitle')}\n\n${t('box.access.codesLabel')}\n${codes.join('\n')}`
      : `${heading}\n${link}\n\n${t('box.access.codesLabel')}\n${codes.join('\n')}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCodes(codeSet);
      showToast(t('box.access.copySuccess'), 'success');
    } catch {
      showToast(t('box.access.copyFailed'));
    }
  }

  const copyButton = (<Button
                variant={copied ? 'secondary' : 'primary'}
                size="large"
                icon={<CopyIcon />}
                className="access-codes__copy"
                onClick={() => void copyAll()}
                testId="box-access-copy"
              >
                {t(account ? 'account.recoveryCopy' : 'box.access.copy')}
              </Button>);
  const actions = (<Cluster
            justify="between"
            align="center"
            gap={4}
            wide
            wrap={false}
            stack="narrow"
            className="access-codes__footer"
          >
            {!verifiedEmail && (
              <Checkbox
                fit
                checked={saved}
                disabled={!copied || saving}
                onCheckedChange={checked => setSavedCodes(checked ? codeSet : null)}
                label={t(account ? 'account.recoverySavedCheckbox' : 'box.access.savedCheckbox')}
                testId="box-access-saved-checkbox"
              />
            )}
            <Button
              variant="primary"
              size="large"
              disabled={saving || ((!copied || !saved) && !verifiedEmail)}
              loading={saving}
              onClick={() => void continueAfterSaving()}
              className="access-codes__continue"
              testId="box-access-action"
            >
              {t(account ? 'box.access.savedButton' : organizer ? 'box.access.continue' : 'box.access.cardContinue')}
            </Button>
          </Cluster>);

  return (
    <Stack
      gap={6}
      wide
      className={classes(
        'access-codes',
        'card-access-claim',
        'card-access-claim--codes',
        copied && 'access-codes--copied',
        variant === 'flat' && 'access-codes--flat',
      )}
      testId="box-access-codes"
    >
      <Stack gap={3} as="header" wide className="access-codes__header">
        {!account && <Badge tone="warning" testId="box-access-badge">
          <span className="access-codes__badge">
            <StarIcon className="access-codes__badge-icon" />
            {subject}
          </span>
        </Badge>}
        <PageHeading title={<>{t(account ? 'account.recoveryTitle' : organizer ? 'box.access.title' : 'box.access.cardTitle')}</>} />
      </Stack>

      <Note tone={verifiedEmail ? 'info' : 'warning'} iconAlign="start" testId="box-access-notice">
        <p>
          <strong>{t(account ? 'account.recoveryTitle' : verifiedEmail ? 'box.access.emailTitle' : 'box.access.noticeTitle')}</strong>
        </p>
        <p>
          {account ? t('account.recoverySaved') : verifiedEmail
            ? t('box.access.emailBody', { email: verifiedEmail })
            : t(organizer ? 'box.access.noticeBody' : 'box.access.cardNoticeBody')}
        </p>
      </Note>

      <Card surface="plain" elevated={variant !== 'flat'} edges={variant === 'flat' ? false : { pieces: 3 }} slot className="access-codes__kit" testId="box-access-kit">
        <Stack gap={5} wide className="access-codes__kit-content">
          {!actionsContainer && <div className="access-codes__link-block">
            <Typography as="p" role="body/sm" className="access-codes__link-label">
              {!account && t(organizer ? 'box.access.linkLabel' : 'box.access.cardLinkLabel')}
            </Typography>
            <div className="access-codes__link-row">
              {!account && <Readout
                className="card-access-claim__link access-codes__link"
                testId="box-access-link"
              >
                {displayUrl(link)}
              </Readout>}
              {copyButton}
            </div>
          </div>}

          {!actionsContainer && <hr className="access-codes__divider" />}

          <div className="access-codes__codes">
            <Cluster justify="between" align="baseline" gap={3} wide wrap={false}>
              <Typography as="p" role="meta/caps" className="access-codes__codes-label">
                {t('box.access.codesLabel')}
              </Typography>
              <Typography as="p" role="meta/helper" className="access-codes__codes-hint">
                {t('box.access.codesHint')}
              </Typography>
            </Cluster>
            <div className="access-codes__codes-well">
              <ol className="access-codes__code-list" data-testid="box-access-code-list">
                {codes.map((code, index) => (
                  <li key={code} className="access-codes__code-item">
                    <span className="access-codes__code-index" aria-hidden="true">
                      {index + 1}
                    </span>
                    <Typography
                      as="span"
                      role="meta/mono"
                      className="access-codes__code-value card-access-claim__code"
                    >
                      {code}
                    </Typography>
                  </li>
                ))}
              </ol>
            </div>
          </div>
          {!actionsContainer && <hr className="access-codes__divider" />}

          {!actionsContainer && actions}
        </Stack>
      </Card>
      {actionsContainer && createPortal(<>{copyButton}{actions}</>, actionsContainer)}
    </Stack>
  );
}
